import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import routes from '@Constants/Routes'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { trackEvent } from '@Utilities/marketingTags'
import HeroBg from '../Assets/hero-bg.jpg'
import HeroPortrait from '../Assets/hero-portrait.jpg'
import { AmberPill, LightOutlinePill, MOBILE } from '../CustomLanding.styled'
import { colors, hero } from '../content'

const Hero: React.FC = () => {
	const navigate = useNavigate()
	const { landing_headline, landing_subheadline } = useAppSelector(s => s.settings)

	const handleSignup = () => {
		trackEvent('CTAClick', { cta_text: 'signup', cta_location: 'hero' })
		navigate(routes.signup)
	}

	const handleLogin = () => {
		trackEvent('CTAClick', { cta_text: 'login', cta_location: 'hero' })
		navigate(routes.login)
	}

	return (
		<Section id="top">
			<BgImage />
			<BgOverlay />
			<Content>
				<TextCol>
					<Badge>
						<BadgeDot />
						{hero.badge}
					</Badge>
					{landing_headline ? (
						<Headline>{landing_headline}</Headline>
					) : (
						<Headline>
							{hero.headlineLine1}
							<br />
							{hero.headlineLine2}
							<Highlight>{hero.headlineHighlight}</Highlight>
							<span>{hero.headlineEmoji}</span>
						</Headline>
					)}
					{landing_subheadline ? (
						<Sub>{landing_subheadline}</Sub>
					) : (
						<Sub>
							{hero.subBefore}
							<strong>{hero.subStrong}</strong>
							{hero.subAfter}
						</Sub>
					)}
					<ExperienceLine>{hero.experienceLine}</ExperienceLine>
					<Actions>
						<AmberPill onClick={handleSignup}>{hero.ctaPrimary}</AmberPill>
						<LightOutlinePill onClick={handleLogin}>{hero.ctaLogin}</LightOutlinePill>
					</Actions>
				</TextCol>
				<PortraitCol>
					<PortraitFrame>
						<PortraitImg src={HeroPortrait} alt={hero.portraitCaption} />
					</PortraitFrame>
					<FloatBadge>
						<FloatBadgeValue>{hero.badgeYears}</FloatBadgeValue>
						<FloatBadgeLabel>{hero.badgeYearsLabel}</FloatBadgeLabel>
					</FloatBadge>
				</PortraitCol>
			</Content>
		</Section>
	)
}

export default Hero

const ifFloat = keyframes`
	0%, 100% { transform: translateY(0); }
	50% { transform: translateY(-10px); }
`

const Section = styled.section`
	position: relative;
	background: ${colors.navy};
	color: ${colors.paper};
	overflow: hidden;
`

const BgImage = styled.div`
	position: absolute;
	inset: 0;
	background-image: url(${HeroBg});
	background-size: cover;
	background-position: center;
	opacity: 0.9;
`

const BgOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: linear-gradient(to left, rgba(11, 28, 70, 0.92) 0%, rgba(11, 28, 70, 0.72) 45%, rgba(11, 28, 70, 0.4) 100%);
	${MOBILE} {
		background: linear-gradient(to top, rgba(11, 28, 70, 0.95), rgba(11, 28, 70, 0.6));
	}
`

const Content = styled.div`
	position: relative;
	max-width: 1180px;
	margin: 0 auto;
	padding: 40px 32px 44px;
	display: flex;
	flex-wrap: wrap;
	gap: 48px;
	align-items: center;
	${MOBILE} {
		display: block;
		padding: 34px 18px 44px;
	}
`

const TextCol = styled.div`
	flex: 1 1 460px;
	min-width: 0;
`

const Badge = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 10px;
	border: 1px solid rgba(255, 184, 28, 0.45);
	background: rgba(255, 184, 28, 0.1);
	color: ${colors.amber};
	border-radius: 999px;
	padding: 8px 18px;
	font-size: 14px;
	font-weight: 600;
	margin-bottom: 28px;
	${MOBILE} {
		gap: 8px;
		padding: 7px 14px;
		font-size: 12px;
		margin-bottom: 20px;
	}
`

const BadgeDot = styled.span`
	width: 7px;
	height: 7px;
	border-radius: 999px;
	background: ${colors.amber};
	display: block;
	${MOBILE} {
		width: 6px;
		height: 6px;
	}
`

const Headline = styled.h1`
	font-weight: 800;
	font-size: clamp(30px, 3.2vw, 46px);
	line-height: 1.3;
	margin: 0 0 20px;
	color: #ffffff;
	text-wrap: balance;
	${MOBILE} {
		font-size: 35px;
		line-height: 1.3;
		margin-bottom: 18px;
	}
`

const Highlight = styled.span`
	color: ${colors.amber};
`

const Sub = styled.p`
	font-size: 17px;
	line-height: 1.9;
	color: rgba(245, 245, 245, 0.8);
	margin: 0 0 16px;
	max-width: 600px;
	strong {
		color: #ffffff;
		font-weight: 600;
	}
	${MOBILE} {
		font-size: 16px;
		line-height: 1.95;
		margin-bottom: 16px;
	}
`

const ExperienceLine = styled.p`
	font-size: 15px;
	font-weight: 600;
	color: #ffffff;
	margin: 0 0 28px;
	border-inline-start: 3px solid ${colors.red};
	padding-inline-start: 16px;
	line-height: 1.8;
	${MOBILE} {
		font-size: 15px;
		margin-bottom: 26px;
		padding-inline-start: 13px;
		line-height: 1.75;
	}
`

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 14px;
	${MOBILE} {
		flex-direction: column;
		gap: 10px;
		margin-bottom: 32px;
	}
`

const PortraitCol = styled.div`
	position: relative;
	flex: 1 1 300px;
	min-width: 0;
	max-width: 400px;
	${MOBILE} {
		max-width: none;
	}
`

/* Back to the design's framed box (4:5 desktop / 5:4 mobile). The portrait
   promo is ~0.79:1, so the desktop window shows it near-uncropped; the mobile
   landscape window anchors to the top so the head stays in frame. */
const PortraitFrame = styled.div`
	position: relative;
	aspect-ratio: 4 / 5;
	border-radius: 28px;
	overflow: hidden;
	border: 1px solid rgba(245, 245, 245, 0.22);
	background-color: #0e2352;
	${MOBILE} {
		aspect-ratio: 5 / 4;
		border-radius: 22px;
	}
`

const PortraitImg = styled.img`
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center;
	${MOBILE} {
		object-position: center top;
	}
`

const FloatBadge = styled.div`
	position: absolute;
	bottom: -26px;
	inset-inline-start: -26px;
	background: ${colors.paper};
	color: ${colors.navy};
	border-radius: 20px;
	padding: 18px 24px;
	box-shadow: 0 18px 40px rgba(8, 20, 50, 0.35);
	animation: ${ifFloat} 6s ease-in-out infinite;
	${MOBILE} {
		bottom: 14px;
		inset-inline-start: 14px;
		border-radius: 16px;
		padding: 12px 18px;
		box-shadow: 0 12px 28px rgba(8, 20, 50, 0.4);
		animation: none;
	}
`

const FloatBadgeValue = styled.div`
	font-weight: 800;
	font-size: 34px;
	line-height: 1;
	color: ${colors.maroon};
	${MOBILE} {
		font-size: 26px;
	}
`

const FloatBadgeLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	margin-top: 4px;
	${MOBILE} {
		font-size: 11px;
		margin-top: 3px;
	}
`
